import { useEffect, useMemo, useState } from "react";
import { Phone, MessageCircle, Users } from "lucide-react";
import { api, telLink, waLink } from "@/lib/api";
import { PageHead, EmptyState } from "@/components/Bits";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SORTS = [
  { value: "date", label: "Date added (newest)" },
  { value: "state", label: "State (A–Z)" },
  { value: "stage", label: "Pipeline stage" },
];

const PERIODS = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "custom", label: "Custom range" },
];

const ymd = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const periodRange = (period, from, to) => {
  const now = new Date();
  if (period === "today") return { start: ymd(now), end: ymd(now) };
  if (period === "week") {
    const d = new Date(now);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return { start: ymd(d), end: ymd(now) };
  }
  if (period === "month") return { start: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), end: ymd(now) };
  if (period === "custom") return { start: from || null, end: to || null };
  return { start: null, end: null };
};

export default function DetailerList() {
  const [rows, setRows] = useState([]);
  const [stages, setStages] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date");
  const [period, setPeriod] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    api.get("/b2b/prospects").then(({ data }) => setRows(data || [])).catch(() => setRows([]));
    api.get("/b2b/stages").then(({ data }) => setStages(data || [])).catch(() => {});
  }, []);

  const stageOrder = useMemo(() => {
    const m = {};
    stages.forEach((s, i) => { m[s] = i; });
    return m;
  }, [stages]);

  const view = useMemo(() => {
    const { start, end } = periodRange(period, from, to);
    const s = search.trim().toLowerCase();
    let list = rows.filter((r) => {
      const added = (r.created_at || "").slice(0, 10);
      if (start && added < start) return false;
      if (end && added > end) return false;
      if (s && ![r.owner_name, r.business_name, r.mobile, r.whatsapp, r.city, r.state]
        .some((v) => (v || "").toLowerCase().includes(s))) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sort === "state") return (a.state || "~").localeCompare(b.state || "~");
      if (sort === "stage") return (stageOrder[a.stage] ?? 99) - (stageOrder[b.stage] ?? 99);
      return (b.created_at || "").localeCompare(a.created_at || ""); // date newest first
    });
    return list;
  }, [rows, search, sort, period, from, to, stageOrder]);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="detailer-list-page">
      <PageHead title="Detailer List" subtitle="All B2B detailers — call or message them directly" />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Input
          data-testid="detailer-search"
          className="min-h-[44px] w-full sm:w-72"
          placeholder="Search owner, studio, number, place…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="min-h-[44px] w-[190px]" data-testid="detailer-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="min-h-[44px] w-[160px]" data-testid="detailer-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" className="min-h-[44px] w-[150px]" data-testid="detailer-from"
              value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            <span className="text-slate-400">to</span>
            <Input type="date" className="min-h-[44px] w-[150px]" data-testid="detailer-to"
              value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          </div>
        )}
        <span className="text-sm text-slate-500" data-testid="detailer-count">{view.length} detailers</span>
      </div>

      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Owner</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Studio</TableHead>
                <TableHead>Place</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Pipeline stage</TableHead>
                <TableHead className="text-right">Reach</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {view.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="p-0">
                    <EmptyState icon={Users} title="No detailers found"
                      hint="Add detailers from the B2B Pipeline, or adjust your filters."
                      testid="detailer-empty" />
                  </TableCell>
                </TableRow>
              )}
              {view.map((r) => {
                const wa = r.whatsapp || r.mobile;
                return (
                  <TableRow key={r.id} data-testid={`detailer-row-${r.id}`}>
                    <TableCell className="font-medium text-brand-ink">{r.owner_name || "—"}</TableCell>
                    <TableCell className="text-slate-500">{r.mobile || "—"}</TableCell>
                    <TableCell className="text-slate-500">{r.business_name || "—"}</TableCell>
                    <TableCell className="text-slate-500">{r.city || "—"}</TableCell>
                    <TableCell className="text-slate-500">{r.state || "—"}</TableCell>
                    <TableCell>
                      <span className="rounded-full bg-brand-mist px-2.5 py-1 text-xs font-medium text-brand-ink">
                        {r.stage || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button asChild size="icon" variant="outline" disabled={!r.mobile}
                          data-testid={`detailer-call-${r.id}`} title="Call">
                          <a href={r.mobile ? telLink(r.mobile) : undefined}>
                            <Phone className="h-4 w-4" />
                          </a>
                        </Button>
                        <Button asChild size="icon" variant="outline" disabled={!wa}
                          data-testid={`detailer-whatsapp-${r.id}`} title="WhatsApp">
                          <a href={wa ? waLink(wa) : undefined} target="_blank" rel="noreferrer">
                            <MessageCircle className="h-4 w-4 text-green-600" />
                          </a>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
